/**
 * DecisionFilters — filter + sort bar for the decisions inbox.
 *
 * Props:
 *   filters         — {source, stakes, urgency, agentId, sort}
 *   onFiltersChange — (newFilters) callback
 *   pendingCount    — total pending (platform + legal)
 *   legalCount      — legal-only count
 *
 * Filter state is URL-synced via useSearchParams.
 */
import { useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { BookOpen } from "lucide-react";

// ── Helpers ───────────────────────────────────────────────────────────────────

function parsedFilters(params) {
  return {
    source: params.get("source") || "all",
    stakes: params.get("stakes") || null,
    urgency: params.get("urgency") || null,
    agentId: params.get("agentId") || null,
    sort: params.get("sort") || "priority",
  };
}

// ── DecisionFilters ───────────────────────────────────────────────────────────

export default function DecisionFilters({
  filters,
  onFiltersChange,
  pendingCount = 0,
  legalCount = 0,
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Sync URL → filters on mount / URL change
  useEffect(() => {
    const parsed = parsedFilters(searchParams);
    // Only call parent if something differs to avoid loops
    const changed = Object.keys(parsed).some(
      (k) => parsed[k] !== (filters[k] || null) && !(parsed[k] === "all" && !filters[k])
    );
    if (changed && onFiltersChange) {
      onFiltersChange(parsed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const updateFilter = useCallback(
    (key, value) => {
      const next = { ...parsedFilters(searchParams), [key]: value };
      // Build URLSearchParams — omit defaults to keep URL clean
      const p = new URLSearchParams();
      if (next.source && next.source !== "all") p.set("source", next.source);
      if (next.stakes) p.set("stakes", next.stakes);
      if (next.urgency) p.set("urgency", next.urgency);
      if (next.agentId) p.set("agentId", next.agentId);
      if (next.sort && next.sort !== "priority") p.set("sort", next.sort);
      setSearchParams(p, { replace: true });
      if (onFiltersChange) onFiltersChange(next);
    },
    [searchParams, setSearchParams, onFiltersChange]
  );

  const currentSource = filters.source || "all";
  const currentStakes = filters.stakes || "";
  const currentSort = filters.sort || "priority";

  const platformCount = pendingCount - legalCount;

  function openPolicies() {
    window.dispatchEvent(new CustomEvent("mc:openPolicies"));
  }

  return (
    <div className="mc-df" role="toolbar" aria-label="Decision filters">
      {/* Source toggle */}
      <div className="mc-df-source-group" role="group" aria-label="Filter by source">
        {[
          { key: "all", label: `All (${pendingCount})` },
          { key: "platform", label: `Platform (${platformCount})` },
          { key: "legal", label: `Legal (${legalCount})` },
        ].map(({ key, label }) => (
          <button
            key={key}
            className={[
              "mc-df-source-btn",
              currentSource === key ? "mc-df-source-btn--active" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => updateFilter("source", key)}
            aria-pressed={currentSource === key}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mc-df-divider" aria-hidden="true" />

      {/* Stakes filter */}
      <label className="mc-df-select-wrap" htmlFor="mc-df-stakes">
        <span className="mc-df-select-label">Stakes</span>
        <select
          id="mc-df-stakes"
          className="mc-df-select"
          value={currentStakes}
          onChange={(e) =>
            updateFilter("stakes", e.target.value || null)
          }
          aria-label="Filter by stakes"
        >
          <option value="">All</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </label>

      {/* Sort */}
      <label className="mc-df-select-wrap" htmlFor="mc-df-sort">
        <span className="mc-df-select-label">Sort</span>
        <select
          id="mc-df-sort"
          className="mc-df-select"
          value={currentSort}
          onChange={(e) => updateFilter("sort", e.target.value)}
          aria-label="Sort decisions"
        >
          <option value="priority">Priority</option>
          <option value="age">Oldest first</option>
          <option value="agent">Agent A–Z</option>
        </select>
      </label>

      {/* Spacer */}
      <div className="mc-df-spacer" aria-hidden="true" />

      {/* Policies icon button */}
      <button
        className="mc-df-policies-btn"
        onClick={openPolicies}
        title="Open policy reference"
        aria-label="Open policies panel"
      >
        <BookOpen size={14} aria-hidden="true" />
        <span className="mc-df-policies-label">Policies</span>
      </button>

      {/* Inline styles */}
      <style>{`
        .mc-df {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          padding: 10px 0;
        }

        /* Source toggle group */
        .mc-df-source-group {
          display: flex;
          align-items: center;
          gap: 0;
          border: 1px solid var(--mc-border);
          border-radius: var(--radius-md, 6px);
          overflow: hidden;
          flex-shrink: 0;
        }

        .mc-df-source-btn {
          padding: 5px 12px;
          font-size: 12px;
          font-family: var(--font-sans);
          font-weight: 500;
          background: transparent;
          border: none;
          border-right: 1px solid var(--mc-border);
          color: var(--color-slate-400);
          cursor: pointer;
          white-space: nowrap;
          transition:
            background-color var(--dur, 150ms) var(--ease, ease),
            color var(--dur, 150ms) var(--ease, ease);
        }

        .mc-df-source-btn:last-child {
          border-right: none;
        }

        .mc-df-source-btn:hover {
          color: var(--mc-text);
          background-color: rgba(255, 255, 255, 0.04);
        }

        .mc-df-source-btn--active {
          background-color: rgba(23, 128, 61, 0.10);
          color: var(--color-green);
        }

        .mc-df-source-btn--active:hover {
          background-color: rgba(23, 128, 61, 0.16);
          color: var(--color-green);
        }

        /* Divider */
        .mc-df-divider {
          width: 1px;
          height: 20px;
          background-color: var(--mc-border);
          flex-shrink: 0;
        }

        /* Select wrappers */
        .mc-df-select-wrap {
          display: flex;
          align-items: center;
          gap: 5px;
          flex-shrink: 0;
        }

        .mc-df-select-label {
          font-size: 11px;
          font-family: var(--font-sans);
          color: var(--color-slate-500);
          text-transform: uppercase;
          letter-spacing: 0.08em;
          white-space: nowrap;
        }

        .mc-df-select {
          appearance: none;
          -webkit-appearance: none;
          background-color: var(--mc-surface);
          border: 1px solid var(--mc-border);
          border-radius: var(--radius-md, 6px);
          color: var(--mc-text);
          font-size: 12px;
          font-family: var(--font-sans);
          padding: 4px 24px 4px 9px;
          cursor: pointer;
          outline: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 7px center;
          transition:
            border-color var(--dur, 150ms) var(--ease, ease);
        }

        .mc-df-select:focus {
          border-color: var(--color-green);
          box-shadow: 0 0 0 2px rgba(23, 128, 61, 0.20);
        }

        .mc-df-select option {
          background-color: var(--mc-surface);
          color: var(--mc-text);
        }

        /* Spacer */
        .mc-df-spacer {
          flex: 1 1 0%;
        }

        /* Policies button */
        .mc-df-policies-btn {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 5px 10px;
          border-radius: var(--radius-md, 6px);
          background: transparent;
          border: 1px solid var(--mc-border);
          color: var(--color-slate-500);
          font-size: 12px;
          font-family: var(--font-sans);
          cursor: pointer;
          transition:
            color var(--dur, 150ms) var(--ease, ease),
            border-color var(--dur, 150ms) var(--ease, ease);
          flex-shrink: 0;
        }

        .mc-df-policies-btn:hover {
          color: var(--mc-text);
          border-color: var(--color-slate-400);
        }

        .mc-df-policies-label {
          font-size: 12px;
        }

        @media (max-width: 640px) {
          .mc-df {
            gap: 6px;
          }

          .mc-df-select-label {
            display: none;
          }

          .mc-df-policies-label {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}
